import React, { useState, useEffect, useContext } from 'react';
import axios from '@common/axios';
import { Button, Empty } from 'antd';

import {
    VideoIcon,
    WidgetIcon,
    TryItIcon,
    ReviewIcon,
    WorksheetIcon,
    WorksheetSolutionIcon,
    DownloadFileIcon,
    TextbookIcon,
    DesmosIcon
} from "@common/icons";

import { BrandContext } from "@context/BrandContext";
import { UserContext } from "@context/UserContext";
import { ContentContext } from "@context/ContentContext";
import CircularLoader from "@common/CircularLoader";
import AdminIdTag, {ADMIN_ID_TAG_TYPES} from "@common/AdminIdTag";

const stripHtml = (html) => html?.replace(/<[^>]*>?/gm, '') || '';

const ResourceItem = ({
                          item,
                          type,
                          category,
                          showIds,
                          conceptId,
                          onAction,
                          customIcon: Icon,
                          iconColor = 'var(--primary-colour)',
                          titleKey = 'mediaName',
                          descKey = 'mediaDescription',
                          idKey = 'mediaId',
                          actionLabel = 'Play'
                      }) => {
    const id = item[idKey];
    const title = item[titleKey];
    const description = item[descKey];
    const titleId = `title-${category}-${id}`;

    return (
        <li style={{ listStyle: 'none' }} className="margin-b--l">
            <article
                className="ch-card small-box-radius rounded padding--l"
                data-concept-id={conceptId}
                data-media-id={id}
                aria-labelledby={titleId}
            >
                {showIds && (
                    <AdminIdTag
                        id={id}
                        type={ADMIN_ID_TAG_TYPES.MEDIA}
                    />
                )}
                <div className="flexRow flexAlignStart mediumGap">
                    <div
                        className="ch-icon-large flex flexCentre flexAlignCentre"
                        style={{ color: iconColor, fill: iconColor, flexShrink: 0 }}
                        aria-hidden="true"
                    >
                        <Icon style={{ width: '100%', height: '100%' }} focusable="false" />
                    </div>

                    <div className="flexColumn flex1">
                        <h3
                            id={titleId}
                            className="ch-h3 margin-b--xs ch-text-b"
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                        <div className="margin-b--m ch-std-font" dangerouslySetInnerHTML={{ __html: description }} />
                        <div>
                            <Button
                                type="primary"
                                className="ch-button"
                                onClick={() => onAction(item, category, id)}
                                aria-label={`${actionLabel}: ${stripHtml(title)}`}
                            >
                                {actionLabel}
                            </Button>
                        </div>
                    </div>
                </div>
            </article>
        </li>
    );
};

const LessonResources = props => {
    const { callback } = props;
    const brandConfig = useContext(BrandContext);
    const { user } = useContext(UserContext);
    const { content, setDialogMediaObject } = useContext(ContentContext);
    const conceptId = content?.currentConcept?.conceptId;
    const [loading, setLoading] = useState(true);
    const [resources, setResources] = useState({});

    useEffect(() => {
        if (!conceptId) {
            setLoading(false);
            return;
        }

        const controller = new AbortController();

        const fetchResources = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/web/ui/lesson/resources/list', {
                    signal: controller.signal
                });
                if (response.data) {
                    setResources(response.data);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error("Failed to load resources", error);
                }
            } finally {
                setLoading(false);
                if (typeof callback === 'function') callback();
            }
        };

        fetchResources();

        return () => controller.abort();
    }, [conceptId, callback]);

    const handlePlay = (item, category, id) => {
        if (category === 'walkthroughList') {
            if (typeof window.loadDialogTryIt === 'function') {
                window.loadDialogTryIt(id, conceptId);
            }
        } else if (category === 'tutorials') {
            if (typeof window.openReviewDialog === 'function') {
                window.openReviewDialog(id, item.tutorialName || item.mediaName);
            }
        } else if (category === 'videos') {
            setDialogMediaObject({ mediaId: id, mediaType: 'VIDEO', mediaName: item.mediaName, conceptId: conceptId });
        } else if (category === 'widgets') {
            setDialogMediaObject({ mediaId: id, mediaType: 'WIDGET', mediaSubType: item.mediaSubType, mediaName: item.mediaName, conceptId: conceptId });
        } else if (category === 'desmoses') {
            const subType = typeof item.mediaSubType === 'object' ? item.mediaSubType?.name : item.mediaSubType;

            if (subType === 'DESMOS_SCIENTIFIC' || subType === 'Scientific Calculator') {
                if (typeof window.loadScientificCalculator === 'function') window.loadScientificCalculator(id);
            } else if (subType === 'DESMOS_MATRIX' || subType === 'Matrix Calculator') {
                if (typeof window.loadMaxtrixCalculator === 'function') window.loadMaxtrixCalculator(id);
            } else {
                setDialogMediaObject({ mediaId: id, mediaType: 'DESMOS', mediaSubType: subType, mediaName: item.mediaName, conceptId: conceptId });
            }
        } else if (['worksheets', 'printLessons', 'textbookList'].includes(category)) {
            if (item.url) {
                window.open(item.url, '_blank');
            } else {
                console.error("No URL available for this resource.");
            }
        }
    };

    if (loading) {
        return <CircularLoader />;
    }

    const hasResources = [
        'videos', 'widgets', 'walkthroughList', 'tutorials',
        'desmoses', 'worksheets', 'printLessons', 'textbookList'
    ].some(key => resources[key] && resources[key].length > 0);

    if (!hasResources) {
        return (
            <div className="flexColumn flexAlignCentre padding-t--xl">
                <Empty description="No resources found" />
            </div>
        );
    }

    const showIds = user?.showResourceIds;

    return (
        <section aria-label="Lesson resources" className="margin-l--m">
            <ul id="resourcesCont" className="ch-list padding--l ch-pageWidth">
                {resources.videos?.map(item => (
                    <ResourceItem
                        key={`video-${item.mediaId}`}
                        item={item}
                        type="Media"
                        category="videos"
                        showIds={showIds}
                        conceptId={conceptId}
                        onAction={handlePlay}
                        customIcon={VideoIcon}
                    />
                ))}

                {resources.widgets?.map(item => (
                    <ResourceItem
                        key={`widget-${item.mediaId}`}
                        item={item}
                        type="Media"
                        category="widgets"
                        showIds={showIds}
                        conceptId={conceptId}
                        onAction={handlePlay}
                        customIcon={WidgetIcon}
                        iconColor="var(--on-surface-mid-colour)"
                    />
                ))}

                {resources.walkthroughList?.map((item, index) => (
                    <ResourceItem
                        key={`walkthrough-${item.questionId}`}
                        item={{
                            ...item,
                            mediaName: `${brandConfig?.walkthroughName || 'Walkthrough'} ${index + 1}`
                        }}
                        type="Walkthrough"
                        category="walkthroughList"
                        showIds={showIds}
                        conceptId={conceptId}
                        idKey="questionId"
                        descKey="description"
                        onAction={handlePlay}
                        customIcon={TryItIcon}
                    />
                ))}

                {resources.tutorials?.map(item => (
                    <ResourceItem
                        key={`tutorial-${item.tutorialId}`}
                        item={{...item, mediaDescription: 'Review content'}}
                        type="Tutorial"
                        category="tutorials"
                        showIds={showIds}
                        conceptId={conceptId}
                        titleKey="tutorialName"
                        idKey="tutorialId"
                        onAction={handlePlay}
                        customIcon={ReviewIcon}
                    />
                ))}

                {resources.desmoses?.map(item => {
                    const subType = typeof item.mediaSubType === 'object' ? item.mediaSubType?.name : item.mediaSubType;
                    const isGeometry = subType === 'Geometry Calculator' || subType === 'DESMOS_GEOMETRY';
                    return (
                        <ResourceItem
                            key={`desmos-${item.mediaId}`}
                            item={item}
                            type="Media"
                            category="desmoses"
                            showIds={showIds}
                            conceptId={conceptId}
                            onAction={handlePlay}
                            customIcon={DesmosIcon}
                            iconColor={isGeometry ? '#1890ff' : 'var(--primary-colour)'}
                        />
                    );
                })}

                {resources.worksheets?.map(item => {
                    const worksheetDesc = item.worksheetSolution
                        ? (brandConfig?.brandText?.worksheetSolutionDesc || "Solution available")
                        : item.mediaDescription;

                    const IconToUse = item.worksheetSolution
                        ? WorksheetSolutionIcon
                        : WorksheetIcon;

                    return (
                        <ResourceItem
                            key={`worksheet-${item.mediaId}`}
                            item={{...item, mediaDescription: worksheetDesc}}
                            type="Media"
                            category="worksheets"
                            showIds={showIds}
                            conceptId={conceptId}
                            onAction={handlePlay}
                            actionLabel="Open"
                            customIcon={IconToUse}
                        />
                    );
                })}

                {resources.printLessons?.map(item => (
                    <ResourceItem
                        key={`print-${item.mediaId}`}
                        item={item}
                        type="Media"
                        category="printLessons"
                        showIds={showIds}
                        conceptId={conceptId}
                        onAction={handlePlay}
                        actionLabel="Open"
                        customIcon={DownloadFileIcon}
                    />
                ))}

                {resources.textbookList?.map(item => {
                    const bookLabel = item.bookName || item.mediaName;
                    const desc = item.authorised
                        ? `View a digital version of the ${bookLabel} textbook.`
                        : `If you have purchased ${bookLabel}, click here to enter your Cambridge GO code and view a digital version of the textbook.`;

                    return (
                        <ResourceItem
                            key={`textbook-${item.mediaId}`}
                            item={{...item, mediaDescription: desc}}
                            type="Media"
                            category="textbookList"
                            showIds={showIds}
                            conceptId={conceptId}
                            onAction={handlePlay}
                            actionLabel="Open"
                            customIcon={TextbookIcon}
                        />
                    );
                })}
            </ul>
        </section>
    );
};

export default LessonResources;